import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Activity } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFD]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-sky-700 text-sky-50 relative overflow-hidden">
        <div className="flex items-center gap-2 relative z-10">
          <Activity className="h-6 w-6 text-sky-200" strokeWidth={1.5} />
          <span className="font-heading text-lg font-semibold">PatientFlow</span>
          <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-sky-600/60 border border-sky-400/60">Beta</span>
        </div>
        <div className="relative z-10 max-w-md mx-auto text-center mb-64">
          <div className="mb-6 rounded-xl overflow-hidden border border-sky-400/30 shadow-lg">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_patient-flow-tracker-2/artifacts/zhokf16i_images%20%283%29.jpg"
              alt="Physiotherapist treating a patient"
              className="w-full h-48 object-cover"
              loading="lazy"
            />
          </div>
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            Track every new patient. Know exactly where they came from.
          </h1>
          <p className="mt-4 text-sky-100/80 leading-relaxed">
            A focused acquisition tracker for physiotherapists. No EMR, no scheduling — just the data you need to grow your clinic.
          </p>
        </div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-sky-400/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <Activity className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">PatientFlow</span>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold text-zinc-900">Sign in</h2>
            <p className="text-sm text-zinc-500 mt-1">Continue tracking your patient acquisition.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="login-password-input" />
            </div>
          </div>
          {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="login-submit-button">
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <p className="text-sm text-zinc-500 text-center">
            New to PatientFlow?{" "}
            <Link to="/register" className="text-emerald-700 font-medium hover:underline" data-testid="login-goto-register">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
